import { Controller } from "@hotwired/stimulus"

// Inline editing for one Measures-tab row (card #2322 follow-up) -- a
// second ENTRY POINT onto the exact same PATCH
// /decks/:id/element/:slide_index/:element_index endpoint the canvas
// right-click Edit flow already posts to (deck_show_editor_controller.js
// #_commit), so a save here is identical persistence
// (result_json["_manual_overrides"]) and versioning, just addressed from
// the Measures tab table instead of the slide canvas.
//
// Mirrors that controller's fetch-then-manual-DOM-update pattern rather than
// a Turbo Stream round trip: update_element renders plain JSON
// (`{ status, element_id, content }`), not a turbo_stream, so there is
// nothing for Turbo to swap in -- this controller reads that JSON response
// straight back into its own row (the value text + the override badge).
//
// CROSS-TAB SYNC (card #2322 follow-up, reported live by Jay): a successful
// save here ALSO reaches into the Report tab's canvas DOM (#_syncCanvasElement
// below) -- the Report and Measures tabs both render once on the SAME page
// load and just toggle visibility client-side (persist-tabs), so without this
// the canvas element sat stale until a full reload. deck_show_editor_controller.js
// does the identical thing in the other direction for a canvas-originated edit.
export default class extends Controller {
  static targets = ["display", "valueText", "input", "textInput", "reasonInput", "badge", "reasonText"]
  static values = { deckId: Number, slideIndex: Number, elementIndex: Number }

  connect() {
    this._original = this.textInputTarget.value
    this._originalReason = this.reasonInputTarget.value
  }

  edit() {
    this.displayTarget.classList.add("hidden")
    this.inputTarget.classList.remove("hidden")
    this.textInputTarget.focus()
    this.textInputTarget.select()
  }

  cancel() {
    this.textInputTarget.value = this._original
    this.reasonInputTarget.value = this._originalReason
    this._reveal()
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }

  // Blank-content guard mirrors DecksController#update_element's own no-op
  // (deck #28 incident): an empty save is never persisted, just reverted --
  // there is nothing worth round-tripping to the server for. Reason (card
  // #2324) carries no such guard -- it never gates the write, only content
  // presence does, matching the server-side rule.
  async save() {
    const content = this.textInputTarget.value
    const reason = this.reasonInputTarget.value

    if (content.trim() === "") {
      this.cancel()
      return
    }

    try {
      const response = await fetch(this._updateUrl(), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({ content, reason }),
      })

      if (!response.ok) throw new Error(`save failed: ${response.status}`)

      const data = await response.json()
      this._original = data.content
      this._originalReason = reason
      this.valueTextTarget.textContent = data.content
      this.badgeTarget.querySelector("span")?.classList.remove("hidden")
      this._updateReasonText(reason)
      this._wireHover(reason)
      this._reveal()
      this._syncCanvasElement(data, reason)
    } catch (error) {
      this.cancel()
    }
  }

  // Always-visible reason line under the value (card #2324 follow-up) --
  // kept in sync with whatever was just saved, the same reveal-on-save
  // treatment the badge line above already gets.
  _updateReasonText(reason) {
    if (!this.hasReasonTextTarget) return

    this.reasonTextTarget.textContent = reason
    this.reasonTextTarget.title = reason
    this.reasonTextTarget.classList.toggle("hidden", !reason)
  }

  // Hover-reveal wiring on the value cell itself (card #2324 follow-up) --
  // reuses DeckOverrideReasonHovercardController, attached here for a
  // FIRST-time override saved in this same page view (a fresh visit would
  // already carry this from DecksHelper#deck_override_hover_attrs). A plain
  // DOM write, not a manual controller instantiation -- Stimulus's own
  // MutationObserver picks up a `data-controller` attribute added after
  // connect and wires the controller up itself.
  _wireHover(reason) {
    const message = reason
      ? `Manually overridden: ${reason}`
      // PARITY: DecksHelper::DECK_OVERRIDE_LOCK_MESSAGE
      : "Manually overridden — protected from the next data Pull"

    this.valueTextTarget.setAttribute("data-controller", "deck-override-reason-hovercard")
    this.valueTextTarget.setAttribute("data-deck-override-reason-hovercard-message-value", message)
    this.valueTextTarget.setAttribute(
      "data-action",
      "mouseenter->deck-override-reason-hovercard#show mouseleave->deck-override-reason-hovercard#hide"
    )
  }

  // Sync the Report tab's canvas after a Measures-tab save (card #2322
  // follow-up, reported live by Jay: the two tabs render once on the SAME
  // page load -- decks/show.html.erb just toggles `hidden` per panel via
  // persist-tabs, they are not separate fetches -- so a save here left the
  // canvas's own copy of this element stale until a full reload. Mirrors
  // deck_show_editor_controller.js#_commit's own DOM update (target.textContent
  // = content) rather than reinventing it -- that controller already proved
  // this is safe to do without a Turbo round trip (update_element renders
  // plain JSON, not a turbo_stream). Addressed via the SAME data-slide-index
  // / data-element-index pair DecksHelper#deck_element_edit_attrs stamps on
  // the canvas element -- every slide stays in the DOM the whole time
  // (application.css .airdrop-viewer__slide is only ever CSS-hidden/off-
  // stage when flipping between slides, never removed), so the target is
  // always there to find regardless of which slide is currently in view.
  // Text-only, matching what the canvas's own commit does to itself -- it
  // does not reveal a lock badge on a FIRST override either
  // (deck_override_lock_badge only ever renders server-side; see
  // deck_show_editor_test.rb's "only appears... after a fresh visit"), so
  // there is no existing badge DOM node here to reveal on that path either.
  // The SAME accepted gap extends to the canvas element's own hover-reveal
  // wiring (card #2324 follow-up) -- a fresh visit picks it up from
  // DecksHelper#deck_element_edit_attrs; this sync path doesn't attempt to
  // wire it live, consistent with the badge it already skips.
  //
  // SCOPED to .airdrop-viewer__deck (deck #28 incident, third round,
  // repeated): airdrop_report_viewer_controller.js#buildFilmstrip clones the
  // WHOLE slide via cloneNode(true) for each filmstrip thumbnail, which
  // duplicates data-slide-index/data-element-index verbatim onto that inert
  // clone -- exactly the same "duplicate id" trap that incident already hit
  // for plain `id` attributes, just on a different attribute pair that
  // wasn't stripped. An UNSCOPED querySelector here matched the filmstrip
  // clone instead of the real slide (it sits earlier in document order --
  // see that controller's own comment), so the edit silently updated the
  // invisible thumbnail while the real canvas stayed stale. Scoping to the
  // real deck container (data-airdrop-report-viewer-target="deck", outside
  // the filmstrip panel entirely) is the same fix shape, applied here rather
  // than widening the clone's own id-stripping (that only touches `id`, not
  // arbitrary data attributes, and stripping every data-* off the clone
  // would be a much wider change than this narrow lookup needs).
  _syncCanvasElement(data, reason) {
    const canvasElement = document.querySelector(
      `.airdrop-viewer__deck [data-slide-index="${this.slideIndexValue}"][data-element-index="${this.elementIndexValue}"]`
    )
    if (!canvasElement) return

    canvasElement.textContent = data.content
    if (data.element_id) canvasElement.dataset.elementId = data.element_id
    // Keep the canvas's own reason pre-fill in sync too (card #2324) -- a
    // FOLLOW-UP canvas edit in the same page view should open with whatever
    // reason was just saved here, not a stale/blank one.
    canvasElement.dataset.overrideReason = reason
  }

  _updateUrl() {
    return `/decks/${this.deckIdValue}/element/${this.slideIndexValue}/${this.elementIndexValue}`
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _reveal() {
    this.displayTarget.classList.remove("hidden")
    this.inputTarget.classList.add("hidden")
  }
}
